<template>
  <div>
    <v-menu
v-model="isMenuOpen"
            open-on-click
            offset-y
            transition="slide-y-transition"
            content-class="filter-menu">
      <!-- Pastille reprise de DataTableFilter, pour rester homogène avec les
           filtres voisins de la barre. -->
      <template #activator="{ props }">
        <v-row class="ma-0 mb-2 filter-pill" v-bind="props">
          <v-col class="pill-label">{{ label }}</v-col>
          <v-col class="pill-value px-0">
            <span class="pill-value-inner">
              <v-icon
v-if="currentRange"
                      class="pill-clear"
                      icon="fas fa-xmark"
                      @click.stop="effacerPeriode">
              </v-icon>
              <span class="pill-text">{{ libelleCourant }}</span>
            </span>
          </v-col>
          <v-col class="pill-icon">
            <!-- `filter-icon-not-active` (style/filter.css) fixe la taille à 12 px et la couleur
                 d'icône par défaut. Sans elle, le chevron garde la taille par défaut de Vuetify et
                 paraît plus gros que celui des filtres voisins. DataTableFilter passe par une
                 méthode `getClass()`, qui renvoie toujours cette même classe : on l'écrit ici en
                 statique, le filtre Période n'ayant pas d'état d'icône variable. -->
            <v-icon class="filter-icon-not-active">
              {{ isMenuOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
            </v-icon>
          </v-col>
        </v-row>
      </template>

      <!-- Liste des raccourcis. On n'utilise PAS la classe `filter-list` des
           filtres à cases : elle borne la hauteur à 150 px avec défilement,
           ce qui suffit à quelques cases mais tronquerait les 7 options. -->
      <v-list class="periode-options px-2">
        <v-list-item
v-for="option in options"
                     :key="option.code"
                     :active="option.code === codeSelectionne"
                     @click.stop="choisirOption(option)">
          <span class="filter-checkbox-label">{{ option.libelle }}</span>
        </v-list-item>
      </v-list>

      <!-- Période personnalisée, dans une liste SŒUR et non imbriquée : c'est
           ainsi que DataTableFilter isole son calendrier, hors de la liste des
           options dont la hauteur est bornée. La plage ne part qu'au clic
           explicite sur « Valider la période », jamais à la simple saisie.

           On n'applique PAS la classe `date-filter-picker` : elle forcerait la
           sélection en turquoise (#2ECCD1, cf. filter.css) alors que les
           calendriers de saisie des contrats — « Jours et heures travaillés »
           notamment — ne passent aucune couleur et gardent donc le gris
           anthracite par défaut de Vuetify. Ne rien imposer ici aligne la
           sélection sur ces calendriers, sans coder de teinte en dur. -->
      <v-list v-if="codeSelectionne === CODE_PERSONNALISEE" class="periode-personnalisee px-2 pb-2">
        <v-locale-provider locale="fr">
          <!-- hide-header : même parti que CreneauxEditorPanel, la bande
               « Sélectionner une date » n'apporte rien dans un filtre. -->
          <v-date-picker
v-model="plagePersonnalisee"
                         multiple="range"
                         hide-header
                         @click.stop>
          </v-date-picker>
        </v-locale-provider>
        <v-row class="ma-0" no-gutters>
          <v-col class="pr-1">
            <v-text-field
label="Du"
                          :model-value="texteDu"
                          readonly
                          hide-details
                          density="compact"
                          variant="outlined">
            </v-text-field>
          </v-col>
          <v-col class="pl-1">
            <v-text-field
label="Au"
                          :model-value="texteAu"
                          readonly
                          hide-details
                          density="compact"
                          variant="outlined">
            </v-text-field>
          </v-col>
        </v-row>
        <div class="pt-2">
          <loader-button
type="button"
                         label="Valider la période"
                         :disabled="!plageComplete"
                         @callback="validerPeriodePersonnalisee">
          </loader-button>
        </div>
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from 'vue';
  import { DateRange, ListChoix } from '@/generated/classes/ListData';
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';

  /** Colonne portant le filtre : le contrat étant sur un mois calendaire,
   *  la période s'applique à sa date de début. */
  const COLONNE = 'dateDebut';
  const CODE_PERSONNALISEE = 'PERSONNALISEE';

  interface OptionPeriode {
    code: string;
    libelle: string;
    /** Bornes de la période, ou null pour « Période personnalisée ». */
    plage: () => { debut: Date; fin: Date } | null;
  }

  /** Premier jour du mois `decalage` mois avant le mois de `reference`. */
  function debutDeMois (reference: Date, decalage = 0): Date {
    return new Date(reference.getFullYear(), reference.getMonth() - decalage, 1);
  }

  /** Dernier jour du mois `decalage` mois avant celui de `reference`.
   *  Le jour 0 du mois suivant = dernier jour du mois visé. */
  function finDeMois (reference: Date, decalage = 0): Date {
    return new Date(reference.getFullYear(), reference.getMonth() - decalage + 1, 0);
  }

  function nomDuMois (date: Date): string {
    return DateFormat.listMonths[date.getMonth()];
  }

  /** Comparaison au jour près : le serveur ne lit que la partie date. */
  function memeJour (a: Date | null, b: Date | null): boolean {
    if (!a || !b) return false;
    const gauche = new Date(a);
    const droite = new Date(b);
    return gauche.getFullYear() === droite.getFullYear()
      && gauche.getMonth() === droite.getMonth()
      && gauche.getDate() === droite.getDate();
  }

  export default defineComponent({
    name: 'SectionContratPeriodeFilter',
    components: { LoaderButton },
    props: {
      /** Plage actuellement appliquée, pour réafficher l'état courant. */
      currentRange: {
        type: Object as PropType<DateRange | null>,
        required: false,
        default: null,
      },
      label: {
        type: String,
        required: false,
        default: 'Période',
      },
    },
    emits: ['apply'],
    data () {
      return {
        CODE_PERSONNALISEE,
        isMenuOpen: false,
        codeSelectionne: '',
        plagePersonnalisee: [] as string[],
      };
    },
    computed: {
      /** Les libellés portent le mois ou l'année concernés, calculés à la volée. */
      options (): OptionPeriode[] {
        const maintenant = new Date();
        const annee = maintenant.getFullYear();
        return [
          {
            code: 'MOIS_DERNIER',
            libelle: `Mois dernier (${nomDuMois(debutDeMois(maintenant, 1))})`,
            plage: () => ({ debut: debutDeMois(maintenant, 1), fin: finDeMois(maintenant, 1) }),
          },
          {
            code: 'MOIS_EN_COURS',
            libelle: `Mois en cours (${nomDuMois(maintenant)})`,
            plage: () => ({ debut: debutDeMois(maintenant), fin: finDeMois(maintenant) }),
          },
          {
            code: 'TROIS_MOIS',
            libelle: '3 derniers mois',
            plage: () => ({ debut: debutDeMois(maintenant, 2), fin: finDeMois(maintenant) }),
          },
          {
            code: 'SIX_MOIS',
            libelle: '6 derniers mois',
            plage: () => ({ debut: debutDeMois(maintenant, 5), fin: finDeMois(maintenant) }),
          },
          {
            code: 'ANNEE_EN_COURS',
            libelle: `Année en cours (${annee})`,
            plage: () => ({ debut: new Date(annee, 0, 1), fin: new Date(annee, 11, 31) }),
          },
          {
            code: 'ANNEE_DERNIERE',
            libelle: `Année dernière (${annee - 1})`,
            plage: () => ({ debut: new Date(annee - 1, 0, 1), fin: new Date(annee - 1, 11, 31) }),
          },
          {
            code: CODE_PERSONNALISEE,
            libelle: 'Période personnalisée',
            plage: () => null,
          },
        ];
      },
      /** Bornes triées de la plage saisie au calendrier (il renvoie tous les jours). */
      bornesPersonnalisees (): { debut: Date; fin: Date } | null {
        if (this.plagePersonnalisee.length === 0) return null;
        const triees = [...this.plagePersonnalisee].sort();
        return {
          debut: this.versDateLocale(triees[0]),
          fin: this.versDateLocale(triees[triees.length - 1]),
        };
      },
      plageComplete (): boolean {
        return this.bornesPersonnalisees != null;
      },
      texteDu (): string {
        return this.bornesPersonnalisees ? DateFormat.getDateLabel(this.bornesPersonnalisees.debut) : '';
      },
      texteAu (): string {
        return this.bornesPersonnalisees ? DateFormat.getDateLabel(this.bornesPersonnalisees.fin) : '';
      },
      /**
       * Libellé de la pastille. On reconnaît un raccourci dont les bornes
       * correspondent à la plage active — l'affichage reste ainsi parlant même
       * quand le filtre vient d'ailleurs (route, rechargement). À défaut, les
       * dates sont affichées en clair, et non le `Date.toString()` brut que
       * produit DataTableFilter pour les colonnes de type date.
       */
      libelleCourant (): string {
        const plage = this.currentRange;
        if (!plage) return 'Tous';
        const debut = new Date(plage.dateDebut);
        const fin = new Date(plage.dateFin);
        const raccourci = this.options.find((option) => {
          const bornes = option.plage();
          return bornes != null && memeJour(bornes.debut, debut) && memeJour(bornes.fin, fin);
        });
        if (raccourci) return raccourci.libelle;
        return `${DateFormat.getDateLabel(debut)} - ${DateFormat.getDateLabel(fin)}`;
      },
    },
    watch: {
      // Réaligne l'option cochée sur la plage réellement appliquée (y compris
      // lorsqu'elle est effacée depuis la pastille ou reposée par la route).
      currentRange: {
        handler (): void {
          if (!this.currentRange) {
            this.codeSelectionne = '';
            this.plagePersonnalisee = [];
          }
        },
        immediate: true,
      },
    },
    methods: {
      /**
       * Le calendrier renvoie des chaînes ; les convertir naïvement décalerait
       * le jour selon le fuseau. Même correction que DataTableFilter.
       */
      versDateLocale (valeur: string): Date {
        const date = new Date(valeur);
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      },
      /** Émet la même charge utile que DataTableFilter : un ListChoix porteur d'un DateRange. */
      emettrePlage (debut: Date, fin: Date, libelle: string): void {
        const plage = new DateRange();
        plage.dateDebut = debut;
        plage.dateFin = fin;
        const choix = new ListChoix();
        choix.dateRange = plage;
        choix.text = libelle;
        this.$emit('apply', [choix], COLONNE);
        this.isMenuOpen = false;
      },
      choisirOption (option: OptionPeriode): void {
        this.codeSelectionne = option.code;
        const bornes = option.plage();
        // « Période personnalisée » n'applique rien : elle ouvre la saisie.
        if (!bornes) return;
        this.emettrePlage(bornes.debut, bornes.fin, option.libelle);
      },
      validerPeriodePersonnalisee (): void {
        const bornes = this.bornesPersonnalisees;
        if (!bornes) return;
        this.emettrePlage(
          bornes.debut,
          bornes.fin,
          `${DateFormat.getDateLabel(bornes.debut)} - ${DateFormat.getDateLabel(bornes.fin)}`,
        );
      },
      /** Un tableau vide est interprété comme une remise à zéro par applyFilters. */
      effacerPeriode (): void {
        this.codeSelectionne = '';
        this.plagePersonnalisee = [];
        this.$emit('apply', [], COLONNE);
        this.isMenuOpen = false;
      },
    },
  });
</script>

<style scoped>
  /* Les 7 raccourcis tiennent sans défilement : on ne borne pas la hauteur,
     contrairement à `filter-list` (150 px) prévue pour de longues listes de
     cases à cocher. */
  .periode-options {
    max-height: none;
    overflow: visible;
  }

  /* Le calendrier impose sa largeur naturelle ; sans ce minimum, le menu se
     règle sur la pastille et le mois s'affiche tronqué. */
  .periode-personnalisee {
    border-top: 1px solid #F1F3F4;
    padding-top: 8px;
    min-width: 328px;
  }
</style>
